import { createStore } from 'vuex';
import PageInterface from '@/interfaces/PageInterface';
import API from '@/api';
import MenuInterface from '@/interfaces/MenuInterface';
import DistrictOfficeInterface from '@/interfaces/DistrictOfficeInterface';
import PageType from '@/classes/PageType';
import ContactPersonInterface from '@/interfaces/ContactPersonInterface';
import PartnerInterface from '@/interfaces/PartnerInterface';
import EventInterface from '@/interfaces/EventInterface';

export default createStore({
  modules: {
    pages: {
      namespaced: true,

      state: {
        status: 0,
        page: null,
        type: 'common',
        error: null,
        hasKlimaheldSection: false,
      },

      mutations: {
        SET_STATUS(state, status: number) {
          state.status = status;
        },
        SET_PAGE(state, page: PageInterface) {
          state.page = page;
        },
        SET_PAGE_TYPE(state, pageType: string) {
          state.pageType = pageType;
        },
        SET_ERROR(state, error) {
          state.error = error;
        },
        SET_HAS_KLIMAHELD_SECTION(state, hasSection: boolean) {
          state.hasKlimaheldSection = hasSection;
        },
      },
      actions: {
        fetchFrontPage({ commit }) {
          commit('SET_STATUS', 1);
          API.getFrontPage()
            .then((response: PageInterface) => {
              commit('SET_ERROR', null);
              commit('SET_PAGE', response);
            }).catch((e) => {
              commit('SET_ERROR', e);
              commit('SET_PAGE', null);
            }).finally(() => {
              commit('SET_STATUS', 0);
            });
        },
        fetchBySlug({ commit }, slug: string) {
          commit('SET_STATUS', 1);
          API.getPage(slug)
            .then((response: PageInterface | null) => {
              commit('SET_ERROR', null);
              commit('SET_PAGE', response);
            }).catch((e) => {
              commit('SET_ERROR', e);
              commit('SET_PAGE', null);
            }).finally(() => {
              commit('SET_STATUS', 0);
            });
        },
        setPageType({ commit }, pageType: number) {
          commit('SET_PAGE_TYPE', PageType.getById(pageType));
        },
        setHasKlimaheldSection({ commit }, hasKlimaheldSection: boolean) {
          commit('SET_HAS_KLIMAHELD_SECTION', hasKlimaheldSection);
        },
      },
      getters: {
        status(state): number {
          return state.status;
        },
        page(state): PageInterface {
          return state.page;
        },
        pageType(state): PageInterface {
          return state.pageType;
        },
        hasKlimaheldSection(state): boolean {
          return state.hasKlimaheldSection;
        },
      },
    },
    menus: {
      namespaced: true,

      state: {
        status: {},
        menus: {},
        errors: {},
      },

      mutations: {
        SET_STATUS(state, data: { slug: string, status: number }) {
          state.status[data.slug] = data.status;
        },
        SET_MENU(state, data: { slug: string, menu: MenuInterface }) {
          state.menus[data.slug] = data.menu;
        },
        SET_ERROR(state, data: { slug: string, error: unknown }) {
          state.errors[data.slug] = data.error;
        },
      },
      actions: {
        fetchBySlug({ commit }, slug: number | string) {
          commit('SET_STATUS', {
            slug,
            status: 0,
          });
          API.getMenu(slug)
            .then((response: MenuInterface) => {
              commit('SET_ERROR', {
                slug,
                error: null,
              });
              commit('SET_MENU', {
                slug,
                menu: response,
              });
            }).catch((e) => {
              commit('SET_ERROR', {
                slug,
                error: e,
              });
              commit('SET_MENU', {
                slug,
                menu: null,
              });
            }).finally(() => {
              commit('SET_STATUS', {
                slug,
                status: 0,
              });
            });
        },
      },
      getters: {
        status: (state) => (slug: string) => {
          const status = state.status[slug];

          return typeof status === 'undefined' ? 0 : status;
        },
        menu: (state) => (slug: string) => {
          const menu = state.menus[slug];

          return typeof menu === 'undefined' ? null : menu;
        },
      },
    },
    sidebar: {
      namespaced: true,

      state: {
        open: false,
      },

      mutations: {
        TOGGLE_SIDEBAR(state) {
          state.open = !state.open;
        },
        OPEN_SIDEBAR(state) {
          state.open = true;
        },
        CLOSE_SIDEBAR(state) {
          state.open = false;
        },
      },
      actions: {
        toggle({ commit }) {
          commit('TOGGLE_SIDEBAR');
        },
        open({ commit }) {
          commit('OPEN_SIDEBAR');
        },
        close({ commit }) {
          commit('CLOSE_SIDEBAR');
        },
      },
      getters: {
        open: (state) => state.open,
      },
    },
    districtOffices: {
      namespaced: true,

      state: {
        status: 0,
        districtOffices: [],
        error: null,
      },

      mutations: {
        SET_STATUS(state, status: number) {
          state.status = status;
        },
        SET_DISTRICT_OFFICES(state, districtOffices: DistrictOfficeInterface[]) {
          state.districtOffices = districtOffices;
        },
        SET_ERROR(state, error: unknown) {
          state.error = error;
        },
      },
      actions: {
        fetchAll({ commit }) {
          commit('SET_STATUS', 0);
          API.getDistrictOffices()
            .then((response: DistrictOfficeInterface[]) => {
              commit('SET_ERROR', null);
              commit('SET_DISTRICT_OFFICES', response);
            }).catch((e) => {
              commit('SET_ERROR', e);
              commit('SET_DISTRICT_OFFICES', []);
            }).finally(() => {
              commit('SET_STATUS', 0);
            });
        },
      },
      getters: {
        status: (state) => (slug: string) => {
          const status = state.status[slug];

          return typeof status === 'undefined' ? 0 : status;
        },
        districtOffice: (state) => (id: number) => {
          const districtOffices = state.districtOffices
            .filter((districtOffice: DistrictOfficeInterface) => districtOffice.id === id);

          return districtOffices.length ? districtOffices[0] : null;
        },
      },
    },
    contactPersons: {
      namespaced: true,

      state: {
        status: 0,
        contactPersons: [],
        error: null,
      },

      mutations: {
        SET_STATUS(state, status: number) {
          state.status = status;
        },
        SET_CONTACT_PERSONS(state, districtOffices: ContactPersonInterface[]) {
          state.contactPersons = districtOffices;
        },
        SET_ERROR(state, error: unknown) {
          state.error = error;
        },
      },
      actions: {
        fetchAll({ commit }) {
          commit('SET_STATUS', 0);
          API.getContactPersons()
            .then((response: ContactPersonInterface[]) => {
              commit('SET_ERROR', null);
              commit('SET_CONTACT_PERSONS', response);
            }).catch((e) => {
              commit('SET_ERROR', e);
              commit('SET_CONTACT_PERSONS', []);
            }).finally(() => {
              commit('SET_STATUS', 0);
            });
        },
      },
      getters: {
        status: (state) => (slug: string) => {
          const status = state.status[slug];

          return typeof status === 'undefined' ? 0 : status;
        },
        contactPerson: (state) => (id: number) => {
          const contactPersons = state.contactPersons
            .filter((contactPerson: ContactPersonInterface) => contactPerson.id === id);

          return contactPersons.length ? contactPersons[0] : null;
        },
      },
    },
    partners: {
      namespaced: true,

      state: {
        status: 0,
        partners: [],
        error: null,
      },

      mutations: {
        SET_STATUS(state, status: number) {
          state.status = status;
        },
        SET_PARTNERS(state, partners: PartnerInterface[]) {
          state.partners = partners;
        },
        SET_ERROR(state, error: unknown) {
          state.error = error;
        },
      },
      actions: {
        fetchAll({ commit }) {
          commit('SET_STATUS', 0);
          API.getPartners()
            .then((response: PartnerInterface[]) => {
              commit('SET_ERROR', null);
              commit('SET_PARTNERS', response);
            }).catch((e) => {
              commit('SET_ERROR', e);
              commit('SET_PARTNERS', []);
            }).finally(() => {
              commit('SET_STATUS', 0);
            });
        },
      },
      getters: {
        status: (state) => (slug: string) => {
          const status = state.status[slug];

          return typeof status === 'undefined' ? 0 : status;
        },
        partner: (state) => (id: number) => {
          const partner = state.partners
            .filter((p: PartnerInterface) => p.id === id);

          return partner.length ? partner[0] : null;
        },
      },
    },
    events: {
      namespaced: true,

      state: {
        status: 0,
        events: [],
        error: null,
      },

      mutations: {
        SET_STATUS(state, status: number) {
          state.status = status;
        },
        SET_EVENTS(state, events: EventInterface[]) {
          state.events = events;
        },
        SET_ERROR(state, error: unknown) {
          state.error = error;
        },
      },
      actions: {
        fetchAll({ commit }) {
          commit('SET_STATUS', 0);
          API.getEvents()
            .then((response: EventInterface[]) => {
              commit('SET_ERROR', null);
              commit('SET_EVENTS', response);
            }).catch((e) => {
              commit('SET_ERROR', e);
              commit('SET_EVENTS', []);
            }).finally(() => {
              commit('SET_STATUS', 0);
            });
        },
      },
      getters: {
        status: (state) => (slug: string) => {
          const status = state.status[slug];

          return typeof status === 'undefined' ? 0 : status;
        },
        all: (state) => state.events,
        current: (state) => state.events.filter((event: EventInterface) => {
          if (event.date) {
            const endOfDay = new Date();
            endOfDay.setUTCHours(0, 0, 0, 0);
            const eventDate = new Date();
            const dateParts = event.date.split('.');
            eventDate.setUTCDate(parseInt(dateParts?.[0], 10));
            eventDate.setUTCMonth(parseInt(dateParts?.[1], 10) - 1);
            eventDate.setUTCFullYear(parseInt(dateParts?.[2], 10));

            return eventDate.getTime() >= endOfDay.getTime();
          }

          return true;
        }),
        past: (state) => state.events.filter((event: EventInterface) => {
          if (event.date) {
            const endOfDay = new Date();
            endOfDay.setUTCHours(0, 0, 0, 0);
            const eventDate = new Date();
            const dateParts = event.date.split('.');
            eventDate.setUTCDate(parseInt(dateParts?.[0], 10));
            eventDate.setUTCMonth(parseInt(dateParts?.[1], 10) - 1);
            eventDate.setUTCFullYear(parseInt(dateParts?.[2], 10));

            return eventDate.getTime() < endOfDay.getTime()
              && event.videoUrl
              && (event.ctaVideo || event.cta);
          }

          return false;
        }),
      },
    },
  },
});
